import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Group, Modal, Text, Title } from "@mantine/core";

import { useAtomValue } from "jotai";

import { AsyncState } from "@prostocheck/controls-web";
import { useReceipt } from "@prostocheck/data-web";
import {
  buildTaxCabinetCheckUrl,
  ERRORS,
  isTranslatableItem,
  receiptAtom,
  receiptErrorAtom,
  receiptLoadingAtom,
} from "@prostocheck/shared";
import { ButtonLink, Card, ReceiptView } from "@prostocheck/ui";

import { ReceiptItems } from "../receipt-items";
import { ReceiptSummary } from "../receipt-summary";
import { IReceiptModalProps } from "./receipt-modal.models";

/**
 * Fetched directly by fiscal number regardless of the register page's loaded
 * list or filter range — unlike a Z-report, ДПС does support a single-receipt
 * read, so a shared `?receipt=` link always resolves.
 */
export const ReceiptModal = ({
  fiscalNumber,
  cashRegisterFn,
  registerName,
  onClose,
}: IReceiptModalProps) => {
  const { t } = useTranslation();
  const { getReceipt } = useReceipt();
  const receiptWithSource = useAtomValue(receiptAtom);
  const isLoading = useAtomValue(receiptLoadingAtom);
  const errorResult = useAtomValue(receiptErrorAtom);

  useEffect(() => {
    if (!fiscalNumber) return;

    void getReceipt(cashRegisterFn, fiscalNumber);
  }, [cashRegisterFn, fiscalNumber, getReceipt]);

  // An offline receipt ДПС does not hold yet is not a failure to retry.
  const isMissingDocument =
    isTranslatableItem(errorResult?.error) &&
    errorResult.error.translationKey === ERRORS.fiscal.documentNotFound;

  const renderContent = () => {
    if (isLoading || (errorResult && !isMissingDocument)) {
      return (
        <AsyncState
          isLoading={isLoading}
          errorResult={errorResult}
          onRetry={() =>
            fiscalNumber && getReceipt(cashRegisterFn, fiscalNumber)
          }
        />
      );
    }

    if (!receiptWithSource) {
      return (
        <Card className="text-center">
          <Title order={3} size="h5">
            {t("receipt.notFound.title")}
          </Title>
          <Text size="sm" c="dimmed" mt="xs">
            {t("receipt.notFound.description")}
          </Text>
        </Card>
      );
    }

    const { receipt, cabinetUrl } = receiptWithSource;
    const taxCabinetUrl =
      cabinetUrl ??
      buildTaxCabinetCheckUrl({
        cashRegisterFn: receipt.cashRegisterFn,
        receiptFn: receipt.fiscalNumber,
        issuedAt: receipt.issuedAt,
        total: receipt.total,
      });

    return (
      <>
        <Group justify="flex-end" mb="md">
          <ButtonLink
            href={taxCabinetUrl}
            target="_blank"
            rel="noreferrer"
            variant="secondary"
          >
            {t("receipt.openInTaxCabinet")}
          </ButtonLink>
        </Group>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr]">
          <ReceiptView receipt={receipt} cashRegisterName={registerName} />
          <Card>
            <Title order={2} size="h5">
              {t("receipt.itemsTitle")}
            </Title>
            <ReceiptItems items={receipt.items} />
            <ReceiptSummary receipt={receipt} />
          </Card>
        </div>
      </>
    );
  };

  return (
    <Modal
      opened={Boolean(fiscalNumber)}
      onClose={onClose}
      size="xl"
      title={t("register.receiptModalTitle")}
      closeButtonProps={{ "aria-label": t("common:close") }}
    >
      {renderContent()}
    </Modal>
  );
};
